import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Sheet } from "../../../design-v2";
import {
  standaloneViewportBottom,
  useKeyboardViewport,
} from "../../../design-v2/components/keyboardViewport";
import ExerciseSearchPanel from "../../workoutBuilder/ExerciseSearchPanel";
import {
  EQUIPMENT_OPTIONS,
  MUSCLE_OPTIONS,
  TYPE_OPTIONS,
} from "../../workoutBuilder/searchFilters";
import {
  getWorkoutLogExercisePicker,
  getWorkoutLogSubstitutePicker,
} from "../api/workoutLogApi";
import { conflictTagLabels, prescribedConflictCopy } from "../domain/substitution";
import { MOTION, motionAllowed } from "../motion";

export const PICKER_EXIT_MS = 180;
export const PICKER_SEARCH_DEBOUNCE_MS = 250;
// Results arrive 25 at a time; the next page is requested when the list is
// scrolled to within this distance of its end.
export const PICKER_PAGE_SIZE = 25;
const PICKER_NEXT_PAGE_DISTANCE_PX = 320;
// Upper bound for the keyboard-closing bridge when no viewport change follows.
export const KEYBOARD_CLOSING_MAX_MS = 1200;
// The sheet's single move back to its keyboard-closed place.
const KEYBOARD_CLOSING_MOVE_MS = 260;

// The Manual Workout Builder's filter UI and option tables, minus Focus and
// Difficulty (§15.7). Each filter is an explicit athlete-selected narrowing of
// the displayed candidates, sent as one query parameter; nothing selected
// sends nothing, so the default picker policy applies untouched.
export const PICKER_FILTERS = Object.freeze([
  { key: "muscle", label: "Muscle", options: MUSCLE_OPTIONS, param: "bodyParts" },
  { key: "equipment", label: "Equipment", options: EQUIPMENT_OPTIONS, param: "equipmentCategory" },
  { key: "type", label: "Type", options: TYPE_OPTIONS, param: "trainingType" },
]);
const NO_FILTERS = Object.freeze({ muscle: [], equipment: [], type: [] });
const NOOP = () => {};

export function pickerFilterQuery(filters) {
  return Object.fromEntries(
    PICKER_FILTERS
      .filter(({ key }) => filters[key]?.length)
      .map(({ key, param }) => [param, filters[key]])
  );
}

// One page of a result set, Add or Substitute alike.
// `executionId` names the open workout, so each row carries the exercise note
// that workout would start it with (carried from saved history).
function fetchPickerPage(substitute, { query, filterQuery, cursor, executionId }) {
  const page = {
    q: query,
    limit: PICKER_PAGE_SIZE,
    ...(cursor ? { cursor } : null),
    ...(executionId ? { executionId } : null),
    ...filterQuery,
  };
  return substitute
    ? getWorkoutLogSubstitutePicker(substitute.record, substitute.item, {
        ...page,
        showAll: Boolean(substitute.showAll),
      })
    : getWorkoutLogExercisePicker(page);
}

function resultCountLabel(total) {
  return total === 1 ? "exercise available" : "exercises available";
}
const PICKER_SKELETON_ROW_COUNT = 8;

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

// Replacing context of the substitute picker (§15.8): the exercise the slot
// holds now, and the prescribed conflict while it is still the planned one.
function SubstituteContext({ substitute }) {
  const { item, presentation, conflict } = substitute;
  if (presentation.kind === "placeholder") {
    return (
      <div className="lz-workout-substitute-context">
        <p className="lz-workout-substitute-context__eyebrow">Planned slot</p>
        <p className="lz-workout-substitute-context__title">Choose an exercise for this slot</p>
      </div>
    );
  }
  const conflictCopy = presentation.substituted ? null : prescribedConflictCopy(conflict);
  return (
    <div className="lz-workout-substitute-context">
      <p className="lz-workout-substitute-context__eyebrow">Replacing</p>
      <p className="lz-workout-substitute-context__title">{item.exerciseName || presentation.originalName}</p>
      {conflictCopy ? (
        <p className="lz-workout-substitute-context__conflict">
          <span aria-hidden="true">⚠</span> {conflictCopy}
        </p>
      ) : null}
    </div>
  );
}

// Add Exercise (default) or, with `substitute`, the Substitute picker of one
// prescribed slot. Ranking and eligibility stay server- and domain-owned; this
// sheet only renders results, the Show all switch and the states around them.
export default function ExercisePickerSheet({
  open,
  online,
  onClose,
  onSelect,
  substitute = null,
  executionId = null,
}) {
  const [query, setQuery] = useState("");
  // Ephemeral picker UI state: never part of the execution document, never
  // persisted, never synced.
  const [filters, setFilters] = useState(NO_FILTERS);
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  // Keyboard open: the rail collapses behind one control in the search field
  // so the results keep the height. Selections are kept either way.
  // The same subscription also reports how much of the sheet's bottom the iOS
  // keyboard covers, so the sheet (and its bottom fade) ends above it. In the
  // installed app the sheet is sized from the large viewport (workoutLog.css),
  // so the covered height is measured against that same edge.
  // It also reports how far iOS panned the visual viewport on focus (it does
  // when the search field sits close to the keyboard, as in the Substitute
  // picker, whose field is one context card lower). The sheet starts that much
  // lower, so its top stays on screen whether or not iOS pans.
  const { open: keyboardReported, inset: reportedCovered, offsetTop: reportedOffset } = useKeyboardViewport({
    enabled: open && online,
    getLayoutBottom: standaloneViewportBottom,
  });
  // Keyboard closing (owner decision): the moment the search field loses focus
  // with the keyboard up, the picker goes back to exactly what it was before
  // the keyboard opened (full sheet, full Replacing card, filter rail), in one
  // smooth move, instead of waiting for iOS to report the closed viewport
  // (which it only does once the keyboard has finished leaving). While
  // `keyboardClosing` is true the sheet is laid out as keyboard-closed; the
  // reported keyboard values are ignored until they confirm it.
  const [keyboardClosing, setKeyboardClosing] = useState(false);
  const keyboardOpen = keyboardReported && !keyboardClosing;
  const coveredBottom = keyboardOpen ? reportedCovered : 0;
  const viewportOffset = keyboardOpen ? reportedOffset : 0;

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);
  // Paging: the server cursor of the next page of the result set on screen
  // (null when it is complete), and whether that page is on its way.
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRequestRef = useRef(null);
  const [request, setRequest] = useState({ key: null, status: "idle" });
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const wasOpenRef = useRef(false);
  const substituteRef = useRef(substitute);
  substituteRef.current = substitute;

  // Where the page was when the picker opened. iOS moves the page behind the
  // sheet when it pans for the keyboard; the modal layer puts it back when the
  // picker unmounts, which showed as a jump after the sheet had gone. The page
  // is put back earlier, while the sheet still covers it: when the keyboard
  // closes, and when a close begins. The modal layer then finds nothing to do.
  const openScrollRef = useRef(null);
  useEffect(() => {
    openScrollRef.current = open ? { x: window.scrollX, y: window.scrollY } : null;
  }, [open]);
  function restorePageScroll() {
    const saved = openScrollRef.current;
    if (saved && (window.scrollX !== saved.x || window.scrollY !== saved.y)) {
      window.scrollTo(saved.x, saved.y);
    }
  }
  useEffect(() => {
    if (open && !keyboardReported) restorePageScroll();
  }, [open, keyboardReported]);

  const pickerClosingRef = useRef(false);
  // Where the sheet's top edge is on screen: with the keyboard closed (read
  // when the field takes focus, before the keyboard opens) and at the moment
  // the field loses focus.
  const closedTopRef = useRef(null);
  const blurTopRef = useRef(null);
  useEffect(() => {
    if (!open) {
      pickerClosingRef.current = false;
      closedTopRef.current = null;
      setKeyboardClosing(false);
      return undefined;
    }
    const sheet = panelRef.current?.closest(".lz-workout-picker");
    if (!sheet) return undefined;
    let timer = null;
    const isField = (target) => Boolean(target?.matches?.("input, textarea"));
    const handleFocusIn = (event) => {
      if (!isField(event.target)) return;
      clearTimeout(timer);
      if (!sheet.hasAttribute("data-keyboard")) closedTopRef.current = sheet.getBoundingClientRect().top;
      setKeyboardClosing(false);
    };
    const beginKeyboardClosing = () => {
      setKeyboardClosing(true);
      // The keyboard did not close after all: go back to what is reported.
      clearTimeout(timer);
      timer = setTimeout(() => setKeyboardClosing(false), KEYBOARD_CLOSING_MAX_MS);
    };
    // A press on the sheet takes focus from the field between its mousedown
    // and its mouseup (iOS sends both, then the click, once the finger lifts).
    // Laying the sheet out keyboard-closed right then moves what is under the
    // finger: the mouseup lands on something else and the browser sends the
    // click to neither. So while a press is in flight the layout is left alone
    // and the return to keyboard-closed starts once its click has been
    // delivered. A blur with no press (the keyboard's own dismiss control)
    // still starts it at once.
    let pressing = false;
    let heldBack = false;
    let releaseTimer = null;
    const handlePress = () => { pressing = true; };
    const handleRelease = () => {
      if (!pressing) return;
      pressing = false;
      // The click of this press is dispatched in the same task as its mouseup:
      // the next task is the first moment after it, whatever handled it.
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        if (!heldBack) return;
        heldBack = false;
        if (pickerClosingRef.current || isField(document.activeElement)) return;
        beginKeyboardClosing();
      }, 0);
    };
    const handleFocusOut = (event) => {
      if (!isField(event.target) || pickerClosingRef.current) return;
      if (!sheet.hasAttribute("data-keyboard")) return;
      blurTopRef.current = sheet.getBoundingClientRect().top;
      if (pressing) heldBack = true;
      else beginKeyboardClosing();
    };
    sheet.addEventListener("focusin", handleFocusIn);
    sheet.addEventListener("focusout", handleFocusOut);
    sheet.addEventListener("mousedown", handlePress, true);
    window.addEventListener("mouseup", handleRelease, true);
    return () => {
      clearTimeout(timer);
      clearTimeout(releaseTimer);
      sheet.removeEventListener("focusin", handleFocusIn);
      sheet.removeEventListener("focusout", handleFocusOut);
      sheet.removeEventListener("mousedown", handlePress, true);
      window.removeEventListener("mouseup", handleRelease, true);
    };
  }, [open]);
  // The viewport confirmed the keyboard is closed: nothing left to bridge.
  useEffect(() => {
    if (!keyboardReported) setKeyboardClosing(false);
  }, [keyboardReported]);
  // The move itself. The sheet is already laid out keyboard-closed; this only
  // decides where it is drawn while it travels from where it was to where it
  // belongs. Two independent parts:
  //   - the planned move, a plain transform animation (it keeps running
  //     smoothly even while the page is busy or frames are sparse, which is
  //     the case while the iOS keyboard animates);
  //   - iOS's own pan, which it undoes at some point without saying when: its
  //     current size is measured, never assumed, and cancelled out through
  //     `translate`, re-measured every frame until it is gone.
  useLayoutEffect(() => {
    if (!keyboardClosing) return undefined;
    const sheet = panelRef.current?.closest(".lz-workout-picker");
    const from = blurTopRef.current;
    const to = closedTopRef.current;
    if (!sheet || from == null || to == null || !motionAllowed()) return undefined;
    const move = sheet.animate(
      [{ transform: `translateY(${from - to}px)` }, { transform: "translateY(0px)" }],
      { duration: KEYBOARD_CLOSING_MOVE_MS, easing: MOTION.easing, fill: "both" }
    );
    const moved = () => {
      const matrix = window.getComputedStyle(sheet).transform;
      const values = matrix && matrix !== "none" ? matrix.match(/matrix.*\((.+)\)/)?.[1].split(",") : null;
      return values ? parseFloat(values[values.length - 1]) || 0 : 0;
    };
    let pan = 0;
    let frame = 0;
    const cancelPan = () => {
      // Where the sheet's box really is, without either correction.
      const raw = sheet.getBoundingClientRect().top - moved() - pan;
      const needed = to - raw;
      if (Math.abs(needed - pan) > 0.5) {
        pan = Math.abs(needed) > 0.5 ? needed : 0;
        sheet.style.translate = pan ? `0 ${pan}px` : "";
      }
    };
    const follow = () => {
      cancelPan();
      frame = window.requestAnimationFrame(follow);
    };
    cancelPan();
    frame = window.requestAnimationFrame(follow);
    return () => {
      window.cancelAnimationFrame(frame);
      move.cancel();
      sheet.style.translate = "";
    };
  }, [keyboardClosing]);

  const substituteItemId = substitute?.item?.itemId || null;
  const showAll = Boolean(substitute?.showAll);
  const filterQuery = useMemo(() => pickerFilterQuery(filters), [filters]);
  // What the athlete asked to see. A retry of the same request is not a new
  // result set and must not move the list.
  const resultSetKey = `${substituteItemId || "add"}|${showAll}|${query}|${JSON.stringify(filterQuery)}`;
  const requestKey = `${resultSetKey}|${retryToken}`;
  const resultSetRef = useRef(null);

  // A substitute picker opened for another slot starts with an empty search
  // and no filters: one slot's narrowing never leaks into another's.
  useEffect(() => {
    setQuery("");
    setFilters(NO_FILTERS);
    setOpenFilterMenu(null);
  }, [substituteItemId]);

  // Each keyboard appearance (and each opening) starts collapsed, with no
  // option menu left open underneath.
  useEffect(() => {
    setFiltersExpanded(false);
    if (keyboardOpen) setOpenFilterMenu(null);
  }, [keyboardOpen, open]);

  useEffect(() => {
    if (open) {
      setClosing(false);
      setOpenFilterMenu(null);
    }
    return () => {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return undefined;
    }
    if (!online) return undefined;

    const isOpening = !wasOpenRef.current;
    wasOpenRef.current = true;
    // Responses of a superseded request (older query, Show all or slot) are
    // ignored: `active` turns false as soon as any input changes.
    let active = true;
    let timer;

    setRequest({ key: requestKey, status: "loading" });
    // Any change of what is asked starts again at the first page; a next page
    // still in flight for the previous result set is dropped when it lands.
    pageRequestRef.current = null;
    setNextCursor(null);
    setLoadingMore(false);
    const load = () => {
      setError("");
      fetchPickerPage(substituteRef.current, { query, filterQuery, cursor: null, executionId }).then((response) => {
        if (!active) return;
        const nextItems = response.items || [];
        setItems(nextItems);
        setNextCursor(response.nextCursor || null);
        // A new result set starts at the top: the list element is reused across
        // searches and filter changes and would otherwise keep the previous
        // list's scroll offset. Presentation only.
        const list = panelRef.current?.querySelector(".lz-builder-search__results");
        if (list && list.scrollTop !== 0 && resultSetRef.current !== resultSetKey) list.scrollTop = 0;
        resultSetRef.current = resultSetKey;
        // The server's filtered total; the page itself is capped at the limit.
        const reported = Number(response.total);
        setTotal(Number.isFinite(reported) && reported >= nextItems.length ? reported : nextItems.length);
        setHiddenCount(Number(response.hiddenByConstraintsCount || 0));
        setRequest({ key: requestKey, status: "success" });
      }).catch(() => {
        if (!active) return;
        setError("Exercises couldn't be loaded. Try again.");
        setRequest({ key: requestKey, status: "error" });
      });
    };

    if (isOpening) load();
    else timer = setTimeout(load, PICKER_SEARCH_DEBOUNCE_MS);

    return () => { active = false; clearTimeout(timer); };
  }, [executionId, filterQuery, online, open, query, requestKey, resultSetKey]);

  const loading = online && (
    request.status === "loading" || request.key !== requestKey
  );

  // Appends the next page. The request is tied to the result set it was made
  // for: if the search, a filter, Show all or the slot changed meanwhile, its
  // answer is ignored. Rows already listed are never listed twice. Nothing
  // here touches the list's scroll position.
  function loadNextPage() {
    if (!open || !online || loading || error || !nextCursor || pageRequestRef.current) return;
    const ticket = { key: requestKey };
    pageRequestRef.current = ticket;
    setLoadingMore(true);
    fetchPickerPage(substituteRef.current, { query, filterQuery, cursor: nextCursor, executionId }).then((response) => {
      if (pageRequestRef.current !== ticket) return;
      const pageItems = response.items || [];
      setItems((current) => {
        const listed = new Set(current.map((exercise) => exercise.exerciseId));
        return [...current, ...pageItems.filter((exercise) => !listed.has(exercise.exerciseId))];
      });
      setNextCursor(response.nextCursor || null);
    }).catch(() => {
      // The rows on screen stay; reaching the end of the list again retries.
    }).finally(() => {
      if (pageRequestRef.current !== ticket) return;
      pageRequestRef.current = null;
      setLoadingMore(false);
    });
  }
  const loadNextPageRef = useRef(loadNextPage);
  loadNextPageRef.current = loadNextPage;

  // Near the end of the list (or a first page too short to scroll): ask for
  // the next one.
  useEffect(() => {
    if (!open || !online || loading || !nextCursor) return undefined;
    const list = panelRef.current?.querySelector(".lz-builder-search__results");
    if (!list) return undefined;
    const check = () => {
      if (list.scrollHeight - list.scrollTop - list.clientHeight <= PICKER_NEXT_PAGE_DISTANCE_PX) {
        loadNextPageRef.current();
      }
    };
    check();
    list.addEventListener("scroll", check, { passive: true });
    return () => list.removeEventListener("scroll", check);
  }, [open, online, loading, nextCursor, items.length]);

  function requestClose(reason) {
    if (closing) return;
    // Closing with the keyboard up: let it go now, with the sheet, and put the
    // page back under the sheet instead of after it.
    pickerClosingRef.current = true;
    setKeyboardClosing(false);
    const active = document.activeElement;
    if (active && panelRef.current?.contains(active)) active.blur();
    restorePageScroll();
    if (prefersReducedMotion()) {
      onClose?.(reason);
      return;
    }
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setClosing(false);
      onClose?.(reason);
    }, PICKER_EXIT_MS);
  }

  function toggleFilterValue(filterKey, value) {
    setFilters((current) => {
      const selected = current[filterKey] || [];
      return {
        ...current,
        [filterKey]: selected.includes(value)
          ? selected.filter((entry) => entry !== value)
          : [...selected, value],
      };
    });
    setOpenFilterMenu(null);
  }

  function clearFilter(event, filterKey) {
    event.stopPropagation();
    setFilters((current) => ({ ...current, [filterKey]: [] }));
    setOpenFilterMenu(null);
  }

  function clearAllFilters() {
    setFilters(NO_FILTERS);
    setOpenFilterMenu(null);
  }

  const activeFilterCount = PICKER_FILTERS.reduce(
    (count, { key }) => count + (filters[key]?.length || 0),
    0
  );
  const filterDisclosure = keyboardOpen ? {
    expanded: filtersExpanded,
    activeCount: activeFilterCount,
    onToggle: () => {
      setFiltersExpanded((expanded) => !expanded);
      setOpenFilterMenu(null);
    },
    onClearAll: clearAllFilters,
  } : null;

  const settled = !loading && !error && request.status === "success";
  const placeholder = substitute?.presentation?.kind === "placeholder";
  const title = !substitute
    ? "Add exercise"
    : placeholder ? "Choose an exercise" : "Substitute Exercise";
  const emptyMessage = !substitute
    ? "No approved exercises found."
    : showAll
      ? "No approved exercises match."
      : hiddenCount > 0
        ? "No matching exercises. Show all includes exercises that conflict with your constraints."
        : "No matching exercises.";

  // Between the filters and the results: Add shows the real filtered total;
  // Substitute shows Show all (when canon renders it) and a small heading. The
  // wrapper is always rendered so the results viewport never shifts.
  const resultsHeader = (
    <div className="lz-workout-picker__results-header">
      {substitute && (hiddenCount > 0 || showAll) ? (
        <div className="lz-workout-picker__show-all">
          <span id={`show-all-${substituteItemId}`} className="lz-workout-picker__show-all-label">
            Show all exercises
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={showAll}
            aria-labelledby={`show-all-${substituteItemId}`}
            className="lz-workout-switch"
            onClick={() => substitute.onShowAllChange?.(!showAll)}
          >
            <span aria-hidden="true" className="lz-workout-switch__thumb" />
          </button>
        </div>
      ) : null}
      {substitute ? (
        <h3 className="lz-workout-picker__results-heading">
          {placeholder ? "Exercises" : "Substitutes"}
        </h3>
      ) : (
        <p className="lz-workout-picker__count" aria-live="polite">
          {settled && total > 0 ? (
            <>
              <strong>{total}</strong> {resultCountLabel(total)}
            </>
          ) : null}
        </p>
      )}
    </div>
  );

  return (
    <Sheet
      open={open}
      onClose={requestClose}
      title={title}
      className={`lz-workout-picker-overlay${closing ? " is-closing" : ""}`}
      surfaceClassName={`lz-workout-picker${substitute ? " lz-workout-picker--substitute" : ""}${closing ? " is-closing" : ""}`}
      data-keyboard={keyboardOpen ? "open" : undefined}
      data-keyboard-closing={keyboardClosing ? "" : undefined}
      style={coveredBottom || viewportOffset ? {
        ...(coveredBottom ? { "--lz-workout-picker-covered-bottom": `${coveredBottom}px` } : null),
        ...(viewportOffset ? { "--lz-workout-picker-viewport-offset": `${viewportOffset}px` } : null),
      } : undefined}
    >
      {substitute ? <SubstituteContext substitute={substitute} /> : null}
      {!online ? <p className="lz-workout-offline-reason">Needs a connection</p> : (
        <>
          <ExerciseSearchPanel
            containerRef={panelRef}
            className="lz-builder-search lz-workout-picker__search"
            searchQuery={query}
            onSearchQueryChange={setQuery}
            onSearchFocus={NOOP}
            onClearSearch={() => setQuery("")}
            disabled={!online}
            shouldShowSearchPanel
            activeSearchTarget={null}
            onCancelActiveSearchTarget={NOOP}
            visibleFilters={PICKER_FILTERS}
            searchFilters={filters}
            openFilterMenu={openFilterMenu}
            onToggleFilterMenu={(filterKey) =>
              setOpenFilterMenu((current) => (current === filterKey ? null : filterKey))}
            onFilterClear={clearFilter}
            onFilterValueToggle={toggleFilterValue}
            resultsHeader={resultsHeader}
            filterDisclosure={filterDisclosure}
            isLoadingExercises={loading}
            exerciseError={error}
            exerciseEmptyMessage={emptyMessage}
            visibleExerciseResults={items}
            onExerciseResultClick={onSelect}
            trackResultScrollEdges={false}
            animatePanelReveal={false}
            skeletonRowCount={PICKER_SKELETON_ROW_COUNT}
            resultsFooter={loadingMore ? (
              <p className="lz-workout-picker__more" role="status">
                <span className="lz-workout-picker__more-dot" aria-hidden="true" />
                Loading more
              </p>
            ) : null}
            getResultTags={substitute ? (exercise) => conflictTagLabels(exercise.conflict) : null}
            resultActionIcon={substitute ? "swap" : "add"}
          />
          {error && !loading ? (
            <Button
              variant="secondary"
              size="sm"
              className="lz-workout-picker__retry"
              onClick={() => setRetryToken((token) => token + 1)}
            >
              Try again
            </Button>
          ) : null}
        </>
      )}
    </Sheet>
  );
}
